import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { authAdminService } from "@/lib/services";

export function ChangePasswordDialog({
  onClose, forced = false,
}: { onClose: () => void; forced?: boolean }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");

  const mut = useMutation({
    mutationFn: () => authAdminService.changePassword(oldPw, newPw),
    onSuccess: () => {
      toast.success("Password updated. Please log in again.");
      // Clear token so we go back to login
      localStorage.removeItem("lfs_erp_token");
      window.location.href = "/login";
    },
    onError: (e: any) => toast.error(e.message || "Could not change password"),
  });

  const canSave = oldPw.length > 0 && newPw.length >= 6 && newPw === confirm && newPw !== oldPw;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !forced) onClose(); }}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
        onInteractOutside={(e) => forced && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" /> {forced ? "Set a new password" : "Change password"}
          </DialogTitle>
          <DialogDescription>
            {forced
              ? "For your security, your account requires a new password before you can continue."
              : "Enter your current password and choose a new one."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {forced ? "Temporary password" : "Current password"}
            </Label>
            <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoFocus />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">New password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            <p className="mt-1 text-[11px] text-muted-foreground">Minimum 6 characters.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            {confirm && newPw !== confirm && (
              <p className="mt-1 text-[11px] text-destructive">Passwords do not match.</p>
            )}
          </div>
        </div>

        <DialogFooter>
          {!forced && <Button variant="outline" onClick={onClose}>Cancel</Button>}
          <Button onClick={() => mut.mutate()} disabled={!canSave || mut.isPending}>
            {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Update password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
